import {HttpErrorResponse, HttpResponse} from '@angular/common/http';
import {FolderUsage} from '../../dtos/folder/folder';

/** entries per Sammelmappe, checked by the backend (FolderServiceImpl.MAX_ITEMS_PER_FOLDER) */
export const MAX_FOLDER_ITEMS = 500;

/**
 * the folder would contain too many entries, or a single request has too many
 */
function isItemLimitError(error: HttpErrorResponse): boolean {
  const details: string = error?.error?.errors?.[0] ?? '';
  return error?.status === 400 && /at most \d+ entries|too many items/.test(details);
}

/**
 * German message for a failed folder request. The error bodies of the folder api are english,
 * so they are mapped by status; `messages` overrides the default text of single statuses.
 */
export function folderErrorMessage(error: HttpErrorResponse, messages: { [status: number]: string } = {}): string {
  if (isItemLimitError(error)) {
    return 'Eine Sammelmappe kann höchstens ' + MAX_FOLDER_ITEMS + ' Einträge enthalten.';
  }
  if (messages[error?.status]) {
    return messages[error.status];
  }
  switch (error?.status) {
    case 0:
      return 'Der Server kann derzeit nicht erreicht werden.';
    case 400:
      return 'Die Anfrage ist ungültig.';
    case 401:
      return 'Sitzung abgelaufen: Melden Sie sich erneut an!';
    case 403:
      return 'Sie haben keine Berechtigung für diese Aktion.';
    case 404:
      return 'Die Sammelmappe oder der Eintrag wurde nicht gefunden.';
    case 429:
      return 'Sie haben zu viele Anfragen gesendet. Bitte versuchen Sie es später erneut.';
    default:
      return 'Es ist ein Fehler aufgetreten.';
  }
}

/**
 * filename from a `Content-Disposition` header, preferring the RFC 5987 encoded `filename*`
 */
export function filenameFromResponse(response: HttpResponse<Blob>, fallback: string): string {
  const disposition = response.headers.get('content-disposition');
  if (!disposition) {
    return fallback;
  }
  const encoded = /filename\*\s*=\s*(?:UTF-8|utf-8)''([^;]+)/.exec(disposition);
  if (encoded) {
    try {
      return decodeURIComponent(encoded[1].trim().replace(/^"|"$/g, ''));
    } catch {
      // malformed encoding, try the plain filename
    }
  }
  const plain = /filename\s*=\s*"?([^";]+)"?/.exec(disposition);
  return plain ? plain[1].trim() : fallback;
}

export function downloadBlob(blob: Blob, filename: string): void {
  const url = window.URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  // link.click() does not work on every firefox version
  link.dispatchEvent(new MouseEvent('click', {bubbles: true, cancelable: true, view: window}));
  setTimeout(() => {
    // firefox needs the url a moment longer
    window.URL.revokeObjectURL(url);
    link.remove();
  }, 100);
}

/**
 * e.g. „In 3 Sammelmappen von 5 Nutzern enthalten.“
 */
export function folderUsageText(usage: FolderUsage): string {
  return 'In ' + usage.folderCount + (usage.folderCount === 1 ? ' Sammelmappe' : ' Sammelmappen')
    + ' von ' + usage.userCount + (usage.userCount === 1 ? ' Nutzer' : ' Nutzern') + ' enthalten.';
}
